import { instantToWallTime } from './instant-to-wall-time';

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/**
 * Convert a wall-clock datetime (`YYYY-MM-DDTHH:mm`) interpreted in `zone` into
 * a fixed UTC instant (ISO 8601). **Target-date DST-aware**: a wall time in
 * March resolves against that date's offset (CDT, UTC-5), not the offset in
 * effect when the value was authored (a February scheduling session). Lifted
 * from admin `fromLocalDatetime`.
 *
 * Iterative solver: guess a UTC instant, see what wall time it lands on in the
 * zone, and correct by the difference. Three passes converge through DST
 * boundaries where two may not.
 *
 * NOTE: this is included for completeness and authoring round-trips. In v1 it is
 * **not wired to any write path** — the admin scheduling flow keeps its existing
 * conversion (BL-596 is display + input only). Falls back to browser-local when
 * no zone is given (matches the legacy helper).
 *
 * @sideEffect none — pure.
 */
export function wallTimeToInstant({
    wallDateTime,
    zone,
}: {
    wallDateTime: string;
    zone?: string;
}): string {
    if (!wallDateTime) return '';
    if (!zone) return new Date(wallDateTime).toISOString();

    const m = wallDateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return new Date(wallDateTime).toISOString();
    const [, yr, mo, dy, hr, mi] = m.map(Number);

    let guess = Date.UTC(yr, mo - 1, dy, hr, mi);
    const target = `${pad(yr, 4)}-${pad(mo)}-${pad(dy)}T${pad(hr)}:${pad(mi)}`;

    for (let i = 0; i < 3; i++) {
        const wt = instantToWallTime({ instant: new Date(guess).toISOString(), zone });
        if (!wt || wt.localDateTime === target) break;
        const diff =
            Date.UTC(wt.year, wt.month - 1, wt.day, wt.hour, wt.minute) -
            Date.UTC(yr, mo - 1, dy, hr, mi);
        guess -= diff;
    }

    return new Date(guess).toISOString();
}
